import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
    ArrowLeftIcon,
    FolderOpenIcon,
    UserCircleIcon,
    CalendarIcon,
    ExclamationTriangleIcon,
    ShieldExclamationIcon,
    ChatBubbleLeftRightIcon,
    DocumentTextIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { SECTION_PERMISSIONS } from "@/config/permissions";
import { usePermissions } from "@/features/auth/usePermissions";
import { getCaseDetails, updateCase } from "../api/fraudSafetyApi";
import { extractApiError } from "@/lib/api-error";
import type { CaseStatus, CasePriority, CaseAction } from "../types";

// ============================================================================
// Badges
// ============================================================================

const PriorityBadge = ({ priority }: { priority: string }) => {
    const p = priority ?? "UNKNOWN";
    const styles: Record<string, string> = {
        CRITICAL: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
        HIGH: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
        MEDIUM: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        LOW: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400",
    };
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium ${styles[p] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            <span className={`h-2 w-2 rounded-full ${p === "CRITICAL" ? "bg-rose-500" : p === "HIGH" ? "bg-orange-500" : p === "MEDIUM" ? "bg-amber-500" : p === "LOW" ? "bg-sky-500" : "bg-slate-400"}`} />
            {p}
        </span>
    );
};

const StatusBadge = ({ status }: { status: string }) => {
    const s = status ?? "UNKNOWN";
    const styles: Record<string, string> = {
        OPEN: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        IN_PROGRESS: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        ESCALATED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
        RESOLVED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        CLOSED: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
    };
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium ${styles[s] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            <span className={`h-2 w-2 rounded-full ${s === "OPEN" ? "bg-blue-500" : s === "IN_PROGRESS" ? "bg-amber-500" : s === "ESCALATED" ? "bg-rose-500" : s === "RESOLVED" ? "bg-emerald-500" : s === "CLOSED" ? "bg-slate-400" : "bg-slate-400"}`} />
            {s.replace("_", " ")}
        </span>
    );
};

const AlertSeverityBadge = ({ severity }: { severity: string }) => {
    const styles: Record<string, string> = {
        CRITICAL: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
        HIGH: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
        MEDIUM: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        LOW: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400",
    };
    return (
        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${styles[severity] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            {severity}
        </span>
    );
};

// ============================================================================
// Helpers
// ============================================================================

const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return "—";
    try {
        return new Intl.DateTimeFormat("en-NG", {
            month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
        }).format(new Date(dateStr));
    } catch {
        return dateStr;
    }
};

const InfoRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex items-start justify-between py-2.5">
        <span className="text-sm text-slate-500 dark:text-slate-400">{label}</span>
        <span className="text-right text-sm font-medium text-slate-900 dark:text-white">{children}</span>
    </div>
);

// ============================================================================
// Modal
// ============================================================================

type ModalProps = { open: boolean; title: string; onClose: () => void; children: React.ReactNode };
const Modal = ({ open, title, onClose, children }: ModalProps) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={onClose} />
            <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-900">
                <h3 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">{title}</h3>
                {children}
            </div>
        </div>
    );
};

// ============================================================================
// Update form options
// ============================================================================

const statusOptions: { value: CaseStatus; label: string }[] = [
    { value: "OPEN", label: "Open" },
    { value: "IN_PROGRESS", label: "In Progress" },
    { value: "ESCALATED", label: "Escalated" },
    { value: "RESOLVED", label: "Resolved" },
    { value: "CLOSED", label: "Closed" },
];

const priorityOptions: { value: CasePriority; label: string }[] = [
    { value: "LOW", label: "Low" },
    { value: "MEDIUM", label: "Medium" },
    { value: "HIGH", label: "High" },
    { value: "CRITICAL", label: "Critical" },
];

const actionOptions: { value: CaseAction | ""; label: string }[] = [
    { value: "", label: "No Action" },
    { value: "ACCOUNT_SUSPENDED", label: "Account Suspended" },
    { value: "ACCOUNT_FROZEN", label: "Account Frozen" },
    { value: "WARNING_ISSUED", label: "Warning Issued" },
    { value: "REVERIFICATION_REQUIRED", label: "Re-verification Required" },
    { value: "NO_ACTION", label: "No Action Needed" },
    { value: "OTHER", label: "Other" },
];

// ============================================================================
// Component
// ============================================================================

export const CaseDetailsRoute = () => {
    const { caseIdOrNumber } = useParams<{ caseIdOrNumber: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { hasPermission } = usePermissions();

    const [updateModal, setUpdateModal] = useState(false);
    const [formStatus, setFormStatus] = useState<CaseStatus>("OPEN");
    const [formPriority, setFormPriority] = useState<CasePriority>("MEDIUM");
    const [formFindings, setFormFindings] = useState("");
    const [formResolution, setFormResolution] = useState("");
    const [formAction, setFormAction] = useState<CaseAction | "">("");
    const [formComment, setFormComment] = useState("");
    const [actionError, setActionError] = useState("");

    const { data, isLoading, error } = useQuery({
        queryKey: ["case-details", caseIdOrNumber],
        queryFn: () => getCaseDetails(caseIdOrNumber!),
        enabled: !!caseIdOrNumber,
    });

    const caseData = data?.data;
    const canManageFraud = hasPermission(SECTION_PERMISSIONS.FRAUD_MANAGE[0]);

    const updateMutation = useMutation({
        mutationFn: () =>
            updateCase(caseData!.caseId, {
                status: formStatus,
                priority: formPriority,
                findings: formFindings || undefined,
                resolution: formResolution || undefined,
                actionTaken: formAction || undefined,
                comment: formComment || undefined,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["case-details", caseIdOrNumber] });
            queryClient.invalidateQueries({ queryKey: ["investigation-cases"] });
            setUpdateModal(false);
            setActionError("");
        },
        onError: (err) => setActionError(extractApiError(err)),
    });

    const openUpdateModal = () => {
        if (caseData) {
            setFormStatus(caseData.status);
            setFormPriority(caseData.priority);
            setFormFindings(caseData.findings || "");
            setFormResolution(caseData.resolution || "");
            setFormAction(caseData.actionTaken || "");
            setFormComment("");
        }
        setUpdateModal(true);
    };

    // ========================================================================
    // Loading / Error
    // ========================================================================

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (error || !caseData) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-400" />
                <h3 className="mt-3 text-lg font-semibold text-rose-700 dark:text-rose-300">Case not found</h3>
                <Button variant="secondary" size="sm" className="mt-4" onClick={() => navigate("/fraud-safety/cases")}>
                    Back to Cases
                </Button>
            </div>
        );
    }

    // ========================================================================
    // Render
    // ========================================================================

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                    <button onClick={() => navigate("/fraud-safety/cases")} className="mt-1 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300">
                        <ArrowLeftIcon className="h-5 w-5" />
                    </button>
                    <div>
                        <div className="flex items-center gap-3">
                            <FolderOpenIcon className="h-7 w-7 text-amber-500" />
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{caseData.title}</h1>
                                <p className="text-sm text-slate-500 dark:text-slate-400">Case #{caseData.caseNumber}</p>
                            </div>
                        </div>
                        {caseData.description && (
                            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{caseData.description}</p>
                        )}
                        <div className="mt-2 flex items-center gap-2">
                            <PriorityBadge priority={caseData.priority} />
                            <StatusBadge status={caseData.status} />
                            <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${caseData.caseType === "FRAUD" ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                                    : caseData.caseType === "SAFETY" ? "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
                                        : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                }`}>
                                {caseData.caseType}
                            </span>
                        </div>
                    </div>
                </div>
                {canManageFraud ? (
                    <Button variant="primary" size="sm" onClick={openUpdateModal}>
                        Update Case
                    </Button>
                ) : null}
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
                {/* Case Info */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                    <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Case Information</h2>
                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        <InfoRow label="Case ID">{caseData.caseId}</InfoRow>
                        <InfoRow label="Case Number">{caseData.caseNumber}</InfoRow>
                        <InfoRow label="Assigned To">{caseData.assignedTo || "Unassigned"}</InfoRow>
                        <InfoRow label="Assigned At">{formatDate(caseData.assignedAt)}</InfoRow>
                        <InfoRow label="Opened">{formatDate(caseData.openedAt)}</InfoRow>
                        <InfoRow label="Escalated">{formatDate(caseData.escalatedAt)}</InfoRow>
                        <InfoRow label="Resolved">{formatDate(caseData.resolvedAt)}</InfoRow>
                        <InfoRow label="Closed">{formatDate(caseData.closedAt)}</InfoRow>
                        <InfoRow label="Created By">{caseData.createdBy || "—"}</InfoRow>
                        <InfoRow label="Updated By">{caseData.updatedBy || "—"}</InfoRow>
                    </div>
                </div>

                {/* User Info & Action */}
                <div className="space-y-6">
                    <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                        <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Primary Subject</h2>
                        <div className="divide-y divide-slate-100 dark:divide-slate-800">
                            <InfoRow label="Name">
                                <div className="flex items-center gap-2">
                                    <UserCircleIcon className="h-4 w-4 text-slate-400" />
                                    {caseData.primaryUserName}
                                </div>
                            </InfoRow>
                            <InfoRow label="Email">{caseData.primaryUserEmail}</InfoRow>
                            <InfoRow label="Type">
                                <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${caseData.primaryUserType === "RIDER" ? "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400" : "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400"}`}>
                                    {caseData.primaryUserType}
                                </span>
                            </InfoRow>
                            <InfoRow label="Profile">
                                <Link to={caseData.primaryUserType === "RIDER" ? `/riders/${caseData.primaryUserId}` : `/drivers/${caseData.primaryUserId}`} className="text-primary-600 hover:underline dark:text-primary-400">
                                    View Profile →
                                </Link>
                            </InfoRow>
                        </div>
                    </div>

                    {/* Action & Resolution */}
                    {(caseData.actionTaken || caseData.findings || caseData.resolution) && (
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                            <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Findings & Resolution</h2>
                            <div className="divide-y divide-slate-100 dark:divide-slate-800">
                                <InfoRow label="Action Taken">
                                    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                        {caseData.actionTaken?.replace(/_/g, " ") || "—"}
                                    </span>
                                </InfoRow>
                                {caseData.findings && (
                                    <div className="py-2.5">
                                        <p className="mb-1 text-sm text-slate-500 dark:text-slate-400">Findings</p>
                                        <p className="text-sm text-slate-900 dark:text-white">{caseData.findings}</p>
                                    </div>
                                )}
                                {caseData.resolution && (
                                    <div className="py-2.5">
                                        <p className="mb-1 text-sm text-slate-500 dark:text-slate-400">Resolution</p>
                                        <p className="text-sm text-slate-900 dark:text-white">{caseData.resolution}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Related Alerts */}
            {caseData.relatedAlerts && caseData.relatedAlerts.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                    <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">
                        <ShieldExclamationIcon className="mr-2 inline h-5 w-5 text-rose-400" />
                        Related Alerts ({caseData.relatedAlerts.length})
                    </h2>
                    <div className="space-y-2">
                        {caseData.relatedAlerts.map((alert) => (
                            <Link
                                key={alert.alertId}
                                to={`/fraud-safety/alerts/${alert.alertId}`}
                                className="flex items-center justify-between rounded-xl border border-slate-100 p-3 transition-colors hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/50"
                            >
                                <div className="flex items-center gap-3">
                                    <ShieldExclamationIcon className="h-5 w-5 text-slate-400" />
                                    <div>
                                        <p className="text-sm font-medium text-slate-900 dark:text-white">{alert.title}</p>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">{alert.alertType?.replace(/_/g, " ")}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <AlertSeverityBadge severity={alert.severity} />
                                    <span className="text-xs text-slate-500 dark:text-slate-400">{formatDate(alert.createdAt)}</span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {/* Case Notes */}
            {caseData.notes && caseData.notes.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                    <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">
                        <ChatBubbleLeftRightIcon className="mr-2 inline h-5 w-5 text-slate-400" />
                        Case Notes ({caseData.notes.length})
                    </h2>
                    <div className="space-y-3">
                        {caseData.notes.map((note) => (
                            <div key={note.noteId} className="rounded-xl border border-slate-100 p-4 dark:border-slate-800">
                                <div className="mb-2 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-400">{note.noteType}</span>
                                        <span className="text-xs text-slate-500 dark:text-slate-400">by {note.createdBy}</span>
                                    </div>
                                    <span className="text-xs text-slate-400">{formatDate(note.createdAt)}</span>
                                </div>
                                <p className="text-sm text-slate-700 dark:text-slate-300">{note.content}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Update Case Modal */}
            <Modal open={updateModal} title="Update Case" onClose={() => setUpdateModal(false)}>
                <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Status</label>
                            <select
                                value={formStatus}
                                onChange={(e) => setFormStatus(e.target.value as CaseStatus)}
                                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                            >
                                {statusOptions.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Priority</label>
                            <select
                                value={formPriority}
                                onChange={(e) => setFormPriority(e.target.value as CasePriority)}
                                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                            >
                                {priorityOptions.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Action Taken</label>
                        <select
                            value={formAction}
                            onChange={(e) => setFormAction(e.target.value as CaseAction | "")}
                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                        >
                            {actionOptions.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Findings</label>
                        <textarea
                            value={formFindings}
                            onChange={(e) => setFormFindings(e.target.value)}
                            rows={3}
                            placeholder="Enter investigation findings…"
                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                        />
                    </div>
                    <div>
                        <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Resolution</label>
                        <textarea
                            value={formResolution}
                            onChange={(e) => setFormResolution(e.target.value)}
                            rows={2}
                            placeholder="Enter resolution summary…"
                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                        />
                    </div>
                    <div>
                        <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Comment</label>
                        <textarea
                            value={formComment}
                            onChange={(e) => setFormComment(e.target.value)}
                            rows={2}
                            placeholder="Add a comment…"
                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                        />
                    </div>
                    {actionError && <p className="text-sm text-rose-600">{actionError}</p>}
                    <div className="flex justify-end gap-3">
                        <Button variant="secondary" size="sm" onClick={() => setUpdateModal(false)}>Cancel</Button>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => updateMutation.mutate()}
                            disabled={updateMutation.isPending}
                        >
                            {updateMutation.isPending ? "Updating…" : "Update Case"}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};
